import { useLayoutEffect, useRef, useState } from "react";
import { useThemeVersion } from "../lib/theme.js";
import { observeSettledSize } from "../lib/settledResize.js";

export default function RenderMount({ factory, className = "" }) {
  const ref = useRef(null);
  const mountRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  // Shared theme observer (lib/theme.js) bumps this on data-theme
  // attribute changes — keeps all canvas renderers re-mounting in
  // lockstep on theme flips without each owning their own
  // MutationObserver.
  const themeVersion = useThemeVersion();

  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const node = ref.current;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      const next = {
        width: Math.max(1, Math.round(rect.width)),
        height: Math.max(1, Math.round(rect.height)),
      };
      setSize((current) =>
        current.width === next.width && current.height === next.height ? current : next,
      );
    };

    updateSize();
    // Burst-coalesced: a divider drag resizes this node every frame,
    // and rebuilding the chart per tick (factory + canvas + texture
    // upload, see the mount effect below) is what froze the drag on
    // slower machines. Mid-burst the previous canvas just stretches
    // with the container (it's styled 100%×100% — a free compositor
    // transform); the settled size triggers one crisp rebuild.
    return observeSettledSize(node, updateSize);
  }, []);

  useLayoutEffect(() => {
    if (!mountRef.current || !size.width || !size.height) return;
    mountRef.current.replaceChildren(factory(size));
  }, [factory, size, themeVersion]);

  return (
    <div
      ref={ref}
      data-render-mount
      className={["relative min-h-0 overflow-hidden", className]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        ref={mountRef}
        className="absolute inset-0 [&>canvas]:block [&>canvas]:h-full [&>canvas]:w-full [&>svg]:block [&>svg]:h-full [&>svg]:w-full"
      />
    </div>
  );
}
